import React, { useState, useEffect } from 'react';
import { NavLink, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Icon, SupportModal } from '../common';
import { messagesService } from '../../services';
import logo from '../../assets/logo.svg';
import styles from '../../styles/components/Sidebar.module.css';

function Sidebar({ user }) {
  const { t } = useTranslation();
  const [avatarError, setAvatarError] = useState(false);
  const [showSupportModal, setShowSupportModal] = useState(false);
  const [unreadCount, setUnreadCount] = useState(0);

  // Fetch unread messages count
  useEffect(() => {
    const fetchUnreadCount = async () => {
      try {
        const response = await messagesService.getUnreadCount();
        if (response.success) {
          setUnreadCount(response.data.count || 0);
        }
      } catch (err) {
        console.error('Error fetching unread count:', err);
      }
    };
    fetchUnreadCount();

    // Refresh every 30 seconds
    const interval = setInterval(fetchUnreadCount, 30000);
    return () => clearInterval(interval);
  }, []);

  const navItems = [
    { path: '/', icon: 'store', label: t('sidebar.myProducts') },
    { path: '/earnings', icon: 'wallet', label: t('sidebar.earnings') },
    { path: '/promotion', icon: 'megaphone', label: t('sidebar.promotion') },
    { path: '/messages', icon: 'message', label: t('sidebar.messages'), badge: unreadCount },
  ];

  const secondaryItems = [
    { path: '/settings', icon: 'settings', label: t('sidebar.settings') },
  ];

  // Get initials from user name (max 2 characters)
  const getInitials = (name) => {
    if (!name) return 'U';
    const parts = name.trim().split(' ').filter(Boolean);
    if (parts.length >= 2) {
      return (parts[0][0] + parts[1][0]).toUpperCase();
    }
    return name.slice(0, 2).toUpperCase();
  };

  // Reset avatar error when user avatar changes
  useEffect(() => {
    setAvatarError(false);
  }, [user?.avatar]);

  return (
    <aside className={styles.sidebar}>
      {/* Logo */}
      <div className={styles.logo}>
        <span className={styles.logoIcon}>
          <img src={logo} alt="MoneMee" width="28" height="28" />
        </span>
        <span className={styles.logoText}>MoneMee</span>
      </div>

      {/* Navigation */}
      <nav className={styles.nav}>
        {/* Main Navigation */}
        <div className={styles.navSection}>
          {navItems.map((item) => (
            <NavLink
              key={item.path}
              to={item.path}
              className={({ isActive }) =>
                `${styles.navItem} ${isActive ? styles.navItemActive : ''}`
              }
            >
              <span className={styles.navIcon}>
                <Icon name={item.icon} size="md" />
              </span>
              <span className={styles.navLabel}>{item.label}</span>
              {item.badge > 0 && (
                <span className={styles.navBadge}>{item.badge}</span>
              )}
            </NavLink>
          ))}
        </div>

        {/* Secondary Navigation */}
        <div className={styles.navSection}>
          <div className={styles.navSectionTitle}>{t('sidebar.sectionSettings')}</div>
          {secondaryItems.map((item) => (
            <NavLink
              key={item.path}
              to={item.path}
              className={({ isActive }) =>
                `${styles.navItem} ${isActive ? styles.navItemActive : ''}`
              }
            >
              <span className={styles.navIcon}>
                <Icon name={item.icon} size="md" />
              </span>
              <span className={styles.navLabel}>{item.label}</span>
            </NavLink>
          ))}
          {/* Hilfe-Center Link */}
          <NavLink
            to="/hilfe"
            className={({ isActive }) =>
              `${styles.navItem} ${isActive ? styles.navItemActive : ''}`
            }
          >
            <span className={styles.navIcon}>
              <Icon name="bookOpen" size="md" />
            </span>
            <span className={styles.navLabel}>{t('sidebar.helpCenter')}</span>
          </NavLink>
          {/* Support Button */}
          <button
            className={styles.navItem}
            onClick={() => setShowSupportModal(true)}
          >
            <span className={styles.navIcon}>
              <Icon name="helpCircle" size="md" />
            </span>
            <span className={styles.navLabel}>{t('sidebar.support')}</span>
          </button>
        </div>
      </nav>

      {/* User Section - Links to Account Settings */}
      <div className={styles.userSection}>
        <Link
          to="/settings?tab=account"
          className={styles.userCard}
        >
          <div className={styles.userAvatar}>
            {user?.avatar && !avatarError ? (
              <img
                src={user.avatar}
                alt={user.name}
                className={styles.userAvatarImage}
                onError={() => setAvatarError(true)}
              />
            ) : (
              <span className={styles.userAvatarInitials}>
                {getInitials(user?.name)}
              </span>
            )}
          </div>
          <div className={styles.userInfo}>
            <div className={styles.userName}>{user?.name || t('sidebar.userFallback')}</div>
            <div className={styles.userRole}>
              {user?.role === 'both' ? t('sidebar.userRoleBoth') :
               user?.role === 'creator' ? t('sidebar.userRoleCreator') : t('sidebar.userRolePromoter')}
            </div>
          </div>
          <span className={styles.userMenuIcon}>
            <Icon name="chevronRight" size="sm" />
          </span>
        </Link>
      </div>

      {/* Legal Links */}
      <div className={styles.sidebarFooter}>
        <nav className={styles.legalLinks}>
          <NavLink to="/impressum" className={styles.legalLink}>{t('sidebar.impressum')}</NavLink>
          <NavLink to="/datenschutz" className={styles.legalLink}>{t('sidebar.datenschutz')}</NavLink>
          <NavLink to="/agb" className={styles.legalLink}>{t('sidebar.agb')}</NavLink>
        </nav>
      </div>

      {/* Support Modal */}
      <SupportModal
        isOpen={showSupportModal}
        onClose={() => setShowSupportModal(false)}
      />
    </aside>
  );
}

export default Sidebar;
